import SngTemplateTable from './components/SnGTemplateTable';
import SnGTemplateModalForm from './components/SnGTemplateModalForm';
import { useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import StandardLayout from '@/common/layouts/StandardLayout';
import { FaPlus } from 'react-icons/fa';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import { toast } from 'react-toastify';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import { ReactNode } from 'react';
import { useGetTournamentsQuery } from '@/redux/api/tournaments';

const TournamentTemplatesPage = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const { updateTournamentTemplate } = useTournamentTemplates({ sng: true });
  const { data: tournaments } = useGetTournamentsQuery({ sng: true });

  const [confirmModalData, setConfirmModalData] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    id: string | null;
    type: string;
    body?: ReactNode | (() => ReactNode);
  } | null>(null);

  const openForm = () => setFormOpen(true);
  const closeForm = () => {
    setFormOpen(false);
    setSelectedTemplateId(null);
  };

  const onSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    openForm();
  };

  const handleDeleteTemplate = async () => {
    if (!confirmModalData?.id) {
      return;
    }

    const activeTournamentsWithTemplate =
      tournaments?.data?.filter((tournament) => tournament.templateId === confirmModalData.id) ?? [];

    if (activeTournamentsWithTemplate?.length > 0) {
      toast.error('Cannot archive template with active tournaments');
      return;
    }

    await updateTournamentTemplate(
      { id: confirmModalData.id, deletedAt: new Date() },
      {
        onSuccess: () => {
          toast.success('Template archived successfully');
          setConfirmModalData(null);
        },
        onError: () => {
          toast.error('Failed to archived template');
        },
      },
    );
  };

  const handleConfirmAction = () => {
    if (confirmModalData?.type === 'delete') {
      handleDeleteTemplate();
      return;
    }
  };

  const onDeleteTemplate = (templateId: string) => {
    setConfirmModalData({
      isOpen: true,
      title: 'Archive Template',
      message: 'Are you sure you want to archive this template?',
      id: templateId,
      type: 'delete',
    });
  };

  return (
    <StandardLayout
      pageTitle="Sit&Go Templates"
      headerButton={
        <Button onClick={openForm} color="primary" className="d-flex align-items-center gap-2">
          <FaPlus />
          <p className="mb-0">Create New Sit&Go Template</p>
        </Button>
      }
    >
      <SngTemplateTable onDeleteTemplate={onDeleteTemplate} onSelectTemplate={onSelectTemplate} />
      {formOpen && <SnGTemplateModalForm onClose={closeForm} selectedTemplateId={selectedTemplateId} />}
      {confirmModalData?.isOpen && (
        <ConfirmModal
          title={confirmModalData.title}
          message={confirmModalData.message}
          body={confirmModalData.body}
          onConfirm={handleConfirmAction}
          disableConfirm={confirmModalData?.type === 'notify'}
          onClose={() => setConfirmModalData(null)}
        />
      )}
    </StandardLayout>
  );
};

export default TournamentTemplatesPage;
